/**
 * @file ColumnLookFields.tsx
 * @description Оформление одной колонки — подгруппа группы «Колонки»
 * (phase19.16, партия SB120, этап 1; uiux-sb120-stage1 §2.5 и
 * «Дополнение» §2.8; данные db-design-sb120-stage1 §1):
 *
 * ```
 * ▸ Колонка 1 · 7/12 · без фона
 *    Фон колонки   [Без фона][Карточка][Плашка]…[Свой]   → items[i].bg
 *    Положение     3×3 + «Растянуть»  ↺ как у всех колонок → items[i].place
 *    Внутри        сверху / снизу / слева-справа ⛓       → items[i].pad
 * ```
 *
 * Положение по вертикали — где колонка стоит в ряду (нет ключа — общее
 * «По вертикали»; «Растянуть» у секции растягивает колонки без своего
 * y), по горизонтали — выравнивание текста колонки (нет ключа — слева;
 * элементы со своим align его сохраняют); совпавшее с умолчанием не пишется.
 */

import { DEFAULT_SITE_THEME, SITE_FILL_ROLES, type SectionColumn, type SectionColumns, type SectionPlace, type SectionPlaceX, type SectionPlaceY } from "@chaiberi/shared";
import { RotateCcw } from "lucide-react";

import { Button } from "../../../ui/button";
import { FieldHint } from "../../../ui/settings-groups";
import { ColorField } from "../../../site-fields/ColorField";
import { PlaceGrid } from "../fields/PlaceGrid";
import { useSectionForm } from "../section-form-context";
import { ColumnPadFields } from "./ColumnPadFields";
import { BG_ROLE_LABELS } from "../../../../lib/site-builder/section-meta";

interface Props {
  column: SectionColumn;
  index: number;
  valign: SectionColumns["valign"];
  onChange: (column: SectionColumn) => void;
}

export function ColumnLookFields({ column, index, valign, onChange }: Props) {
  const { theme } = useSectionForm();
  const t = theme ?? DEFAULT_SITE_THEME;
  const y = column.place?.y ?? valign;
  const x = column.place?.x ?? "left";

  function setPlace(place: { y: SectionPlaceY; x: SectionPlaceX } | undefined) {
    const next = { ...column };
    const stored: SectionPlace = {};
    if (place && place.y !== valign) stored.y = place.y;
    if (place && place.x !== "left") stored.x = place.x;
    if (Object.keys(stored).length > 0) next.place = stored;
    else delete next.place;
    onChange(next);
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <ColorField
        mode="fill"
        label={`Фон колонки ${index + 1}`}
        value={column.bg ?? null}
        none={{ label: "Без фона" }}
        roles={SITE_FILL_ROLES}
        labels={BG_ROLE_LABELS}
        colors={t.colors}
        onChange={(fill) => {
          const next = { ...column };
          if (fill) next.bg = fill;
          else delete next.bg;
          onChange(next);
        }}
      />
      <div className="flex min-w-0 flex-col gap-1">
        {column.place ? (
          <Button variant="ghost" size="sm" className="h-12 self-end can-hover:h-8" onClick={() => setPlace(undefined)} aria-label={`Колонка ${index + 1}: положение как у всех колонок`}>
            <RotateCcw aria-hidden />
            как у всех колонок
          </Button>
        ) : null}
        <PlaceGrid label={`Положение колонки ${index + 1}`} y={y} x={x} onChange={setPlace} />
        <FieldHint className="mt-0">По вертикали — где колонка стоит в ряду. По горизонтали — выравнивание текста колонки; элементы со своим выравниванием его сохраняют.</FieldHint>
      </div>
      <ColumnPadFields column={column} index={index} density={t.spacing.density} onChange={onChange} />
    </div>
  );
}
